import * as Sentry from '@sentry/react';
import type { Session } from './createSession';

/**
 * How long we wait for a single `initializeMapView` call before giving up on
 * it. Mirrors `checkSessionValidWithTimeout`'s default (see
 * `libs/websockets/src/lib/ol-server/checkSessionValid.ts`) — the codebase's
 * one existing convention for "how long to wait on a bare `session.request`
 * that has no timeout of its own". `getOverlayMP`'s underlying
 * `JSONRPCClient` (see `createSession.ts`) has no built-in timeout: pending
 * calls are only settled when the socket closes or is retired, never while
 * it stays open — so without this, a request that's sent but never answered
 * on an open socket (e.g. a replica that's still warming up mid-rollout)
 * would hang `primeMapView` forever. On the
 * reconnect path that blocks `setSessions` indefinitely, turning a
 * would-be silent recovery into a forced logout once the grace period
 * deadline elapses with the session never advertised.
 */
const PRIME_MAP_VIEW_TIMEOUT_MS = 8000;

/**
 * The backend requires a server-side "mapview" to exist on an `olSession`
 * before most other RPCs (settings, permissions, targets, ...) will work —
 * without one it returns `wsinterface.cpp: no mapview: internal error`
 * (CLASSIC-10280). `initializeMapView` is the callback that primes it (real
 * implementation in `apps/ais/src/main.tsx`, injected as a prop to avoid
 * `libs/auth` depending on `libs/websockets`).
 *
 * This is the ONE place that calls that callback. Every code path that
 * establishes an `olSession` (page-load restore in `tryRestoreSessionById`,
 * mid-session reconnect in `useReconnect`) must call `primeMapView` here
 * instead of calling `initializeMapView` directly — that's what keeps their
 * error-handling and Sentry tagging from silently drifting apart as either
 * call site evolves.
 *
 * Always best-effort and bounded: `initializeMapView` failing — or simply
 * never responding — is unfortunate (the session will keep hitting "no
 * mapview" errors until something else primes it) but must never itself be
 * treated as "the session is invalid", and must never block its caller
 * indefinitely. The failure conflation is what used to bounce a perfectly
 * good `tryRestoreSessionById` restore to the login screen over a transient
 * priming blip; the indefinite-hang risk is what could otherwise stall
 * `setSessions` on the reconnect path until the grace period gives up.
 * `useGracefulLogout` remains the sole authority on when an `olSession`
 * problem is serious enough to log the user out.
 *
 * Resolves `true` when the mapview is primed (or there is nothing to prime)
 * and `false` when priming failed, so a caller that already holds a working
 * socket can decline to swap it for one that could not be primed.
 */
export async function primeMapView(
  initializeMapView: ((session: Session) => Promise<void>) | undefined,
  session: Session,
  context: 'restore' | 'reconnect',
): Promise<boolean> {
  if (!initializeMapView) {
    return true;
  }

  try {
    await Promise.race([
      initializeMapView(session),
      new Promise<never>((_, reject) => {
        setTimeout(
          () =>
            reject(
              new Error(
                `initializeMapView timed out after ${PRIME_MAP_VIEW_TIMEOUT_MS}ms`,
              ),
            ),
          PRIME_MAP_VIEW_TIMEOUT_MS,
        );
      }),
    ]);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { step: 'initializeMapView', context },
      extra: { sessionId: session.sessionId },
    });
    return false;
  }
}
